// Centralized API Service for IP Protection Center

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

let cachedGeoLocation = null;

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data
 */
export const fetchGeoLocation = async () => {
  // Return cached data if available
  if (cachedGeoLocation) {
    return cachedGeoLocation;
  }

  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    const data = await response.json();
    
    if (data.isSuccess && data.statusCode === 200) {
      cachedGeoLocation = data;
      return data;
    } else {
      console.error('Geolocation API error:', data.errorMessage || data.message || 'Unknown error');
      return null;
    }
  } catch (error) {
    console.error('Failed to fetch geolocation:', error);
    return null;
  }
};

/**
 * Extract dial code from country code string (e.g., "+1" from "+1")
 * @param {string} countryCode - Country code with dial code (e.g., "+1")
 * @returns {number} Dial code as number
 */
const extractDialCode = (countryCode) => {
  const match = countryCode.match(/\+(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
};

/**
 * Submit lead data to CRM
 * @param {Object} formData - Form data object
 * @param {Object} options - Additional options (packageTitle, packageAmount)
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData, options = {}) => {
  try {
    // Get geolocation data
    const geoData = await fetchGeoLocation();
    
    // Extract numeric phone number only (remove any non-digit characters)
    const phoneNumber = formData.phone.replace(/\D/g, '');
    
    // Extract dial code from country code
    const countryDialCode = extractDialCode(formData.countryCode);
    
    // Get current page URL
    const pageUrl = window.location.href;
    
    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: formData.country || (geoData?.data?.countryCode || 'US'),
      phoneNumber: phoneNumber,
      ipAddress: geoData?.data?.ipAddress || '',
      description: formData.message || formData.description || '',
      pageUrl: pageUrl,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: options?.packageTitle || '',
      packageAmount: options?.packageAmount || 0
    };

    // Submit to lead API
    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    // Handle different status codes
    if (response.status === 200 && result.isSuccess) {
      // Success - redirect to application page
      return {
        success: true,
        customerId: result.data.customerId,
        message: result.data.message || 'Lead created successfully'
      };
    } else if (response.status === 401) {
      // Unauthorized
      return {
        success: false,
        error: result.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      // Bad request
      return {
        success: false,
        error: result.errorMessage || 'Invalid data submitted'
      };
    } else {
      // Other errors
      return {
        success: false,
        error: result.errorMessage || result.message || 'Something went wrong'
      };
    }
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      error: 'Failed to submit. Please try again.'
    };
  }
};

/**
 * Get country code from geolocation
 * @returns {Promise<string>} Country code (e.g., "US", "PK")
 */
export const getCountryCode = async () => {
  const geoData = await fetchGeoLocation();
  return geoData?.data?.countryCode || 'US';
};

/**
 * Get country name from geolocation
 * @returns {Promise<string>} Country name
 */
export const getCountryName = async () => {
  const geoData = await fetchGeoLocation();
  return geoData?.data?.countryName || 'United States';
};
